import { LoadFailed, QueryLoading } from '../components/QueryState';
import { useEffect } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canEditPayeeAccounts,
  formatPeriod,
  nextPeriodStart,
  nzDate,
  periodStartFor,
  previousPeriodStart,
} from '@promanage/core';
import { Button, Chip, Field, Panel, PanelHeader, Ref, Select } from '@promanage/ui';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { Icon } from '../components/Icon';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { useOrgSettings } from '../features/settings/org';
import { useSession } from '../features/auth/session';
import {
  STATE_LABELS,
  STATE_TONES,
  coverageLine,
  documentState,
  fileSize,
  outstandingLine,
  runDetail,
  runHeadline,
  runInProgress,
  runTone,
  summarise,
  type DeliveryDocument,
  type DocumentState,
} from '../features/reports/delivery';
import { DeliveryLines, when } from '../features/reports/DeliveryLines';
import { downloadDocument } from '../features/reports/downloadDocument';
import { parseDeliverySearch } from '../features/reports/deliverySearch';
import { needsStepUp, trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/delivery')({
  validateSearch: parseDeliverySearch,
  component: DeliveryPage,
});

/**
 * Statement delivery — a whole month, and where every copy of it went.
 *
 * ── WHAT THIS SCREEN IS FOR, AND WHY IT IS NOT `/reports/statements` ──────────────────────────
 *
 * That page is ONE owner and ONE month: the document, on screen, to be read before it goes out.
 * This is the month as a job — forty owners, produced, filed, sent, and the three or four where
 * something went wrong. They are different questions asked by the same person on the same evening,
 * and one screen answering both would answer neither well: a document needs the whole width, and a
 * job needs a list.
 *
 * ── THE LIST IS ORDERED BY WHAT NEEDS DOING ───────────────────────────────────────────────────
 *
 * Failures first, then owners with nobody to send to, then everything that worked. That is the
 * opposite of alphabetical and it is deliberate: this screen's job is to say whether there is work
 * left, and a list that opens on thirty-eight successes buries the two that are not.
 *
 * ── PRODUCING AND SENDING ARE TWO BUTTONS ─────────────────────────────────────────────────────
 *
 * Producing files documents an operator can read and is the ordinary month-end job. Sending puts
 * them in owners' mailboxes and cannot be undone, so it is a separate act behind a higher
 * authority. One button that did both would be a button somebody presses to have a look.
 */
function DeliveryPage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const session = useSession();
  const org = useOrgSettings();

  /*
   * The default month is the one JUST GONE, not the one we are in — which is the opposite of
   * `/reports/statements` and is right for the opposite reason. That page produces a document on
   * demand, often mid-month. This page is the month-end job, and the job is always about the month
   * that has finished.
   */
  const today = nzDate(new Date());
  const thisMonth = periodStartFor(today);
  const period = selection.period ?? previousPeriodStart(thisMonth);

  const documents = useQuery(
    trpc.delivery.forPeriod.queryOptions(
      { period },
      { meta: { errorTitle: "Could not read this month's documents" }, staleTime: 10_000 },
    ),
  );

  /*
   * ── THE RUN, POLLED WHILE IT IS RUNNING ────────────────────────────────────
   *
   * Two seconds while the worker has it, and NOT AT ALL once it has finished. A poll that carried
   * on would be a request every two seconds for as long as the tab is open, on a screen an
   * operator leaves open for the evening — principle 5, and the difference between a screen that
   * follows a job and a screen that busies a server.
   *
   * It is a QUERY rather than the mutation's own result, which is what makes a failure survive a
   * reload: the mutation forgets it succeeded the moment the page is refreshed, and the run it
   * started is exactly the thing somebody comes back to look at.
   */
  const runStatus = useQuery(
    trpc.delivery.runStatus.queryOptions(
      { period },
      {
        meta: { errorTitle: 'Could not read production progress' },
        refetchInterval: (query) => (runInProgress(query.state.data) ? 2_000 : false),
        staleTime: 0,
      },
    ),
  );

  const status = runStatus.data ?? null;

  /*
   * The documents are re-read when the run STOPS running, not on a timer. A finished run is the
   * only moment the list can have changed underneath, and one invalidation at that moment is
   * cheaper and less surprising than polling two lists.
   */
  const finishedState = status !== null && !runInProgress(status) ? status.state : null;
  useEffect(() => {
    if (finishedState === null) return;
    void queryClient.invalidateQueries({ queryKey: trpc.delivery.forPeriod.queryKey() });
  }, [finishedState, status?.finishedAt, queryClient]);

  const maySend = canEditPayeeAccounts(session?.roles ?? []);
  // Somewhere for a reply to go — the agency's ProManage inbox or its own address (0143).
  const replyToRecorded = org.data?.replies.address != null;

  const run = useMutation(
    trpc.delivery.run.mutationOptions({
      meta: { errorTitle: 'Could not start the run' },
      onSuccess() {
        /*
         * The run is ENQUEUED, not done. Both queries are invalidated: the status so the panel
         * below starts following this run within the moment rather than after the next poll, and
         * the list because a re-run of an already-produced month can finish before either.
         */
        void queryClient.invalidateQueries({ queryKey: trpc.delivery.runStatus.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.delivery.forPeriod.queryKey() });
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  const rows = documents.data ?? [];
  const summary = summarise(rows);
  const outstanding = outstandingLine(summary);

  const step = (months: -1 | 1) => {
    void navigate({
      search: { period: months === 1 ? nextPeriodStart(period) : previousPeriodStart(period) },
    });
  };

  return (
    <ReportWorkspace
      title="Statement delivery"
      subtitle="One month, every owner — what was produced, and who has it"
    >
      <div className="flex flex-col gap-4">
        {runStatus.isError ? (
          <LoadFailed retry={() => void runStatus.refetch()} retrying={runStatus.isFetching} />
        ) : null}
        {runStatus.isPending ? <QueryLoading label="Reading production progress..." /> : null}
        {/* ── The month, and the two acts ──────────────────────────────────── */}
        <Panel className="flex flex-wrap items-end justify-between gap-4 p-5">
          <div className="flex items-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              aria-label="Previous month"
              onClick={() => step(-1)}
            >
              <Icon name="chevron-right" size={14} className="rotate-180" />
            </Button>

            <Field label="Month">
              {(props) => (
                <Select
                  {...props}
                  value={period}
                  onChange={(e) => void navigate({ search: { period: e.target.value } })}
                >
                  {monthsBackFrom(thisMonth, 18).map((value) => (
                    <option key={value} value={value}>
                      {formatPeriod(value)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Button
              size="sm"
              variant="secondary"
              aria-label="Next month"
              // A month that has not finished has no month-end job. Disabled rather than hidden,
              // so the control does not move under the cursor while stepping through a year.
              disabled={period >= previousPeriodStart(thisMonth)}
              onClick={() => step(1)}
            >
              <Icon name="chevron-right" size={14} />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={run.isPending}
              onClick={() => run.mutate({ period, send: false })}
            >
              {run.isPending ? 'Starting' : 'Produce'}
            </Button>

            <Button
              disabled={run.isPending || !maySend || !replyToRecorded}
              onClick={() => run.mutate({ period, send: true })}
            >
              Produce and send
            </Button>
          </div>
        </Panel>

        {/*
         * ── WHY SENDING IS UNAVAILABLE, WHEN IT IS ────────────────────────────
         *
         * A disabled button with no explanation is the worst control on any screen: the operator
         * cannot tell whether they lack a permission, whether something is missing, or whether the
         * product is broken. Both reasons are named, and the fixable one links to its own page.
         */}
        {!replyToRecorded ? (
          <Panel className="border-l-2 border-l-warn px-5 py-4">
            <p className="text-[0.8125rem]">
              <strong className="font-semibold">Nothing can be sent yet.</strong> Replies have
              nowhere to go — claim an inbox address or record your own in{' '}
              <Link to="/settings/delivery" className="text-accent hover:underline">
                Settings → Delivery
              </Link>{' '}
              first. An owner who has a question about their statement replies to the email, and an
              address that discards those is worse than no email at all.
            </p>
          </Panel>
        ) : !maySend ? (
          <Panel className="px-5 py-4">
            <p className="text-[0.8125rem] text-ink-muted">
              You can produce this month’s statements here. Sending them to owners needs a trust
              administrator — the same authority that changes where an owner’s money is paid.
            </p>
          </Panel>
        ) : null}

        {/*
         * ── THE RUN ───────────────────────────────────────────────────────────
         *
         * SAID AS "STARTED", NOT "DONE", while it is going. Rendering forty PDFs takes minutes and
         * happens in the worker; a screen that claimed completion here would be lying for most of
         * it, and the operator would go home.
         *
         * IT OUTLIVES THE BUTTON PRESS. This is read from the job rather than from the mutation,
         * so a run that failed an hour ago is still on the screen when somebody comes back to see
         * why nothing arrived — which was the whole reason this panel exists.
         */}
        {run.isSuccess && run.data.alreadyRunning && runInProgress(status) ? (
          <Panel className="px-5 py-4">
            <p className="text-[0.8125rem] text-ink-muted">
              That month was already running, so nothing new was started. Its progress is below.
            </p>
          </Panel>
        ) : null}

        {status ? (
          <Panel
            className={
              status.state === 'failed'
                ? 'border-l-2 border-l-danger px-5 py-4'
                : status.state === 'completed'
                  ? 'px-5 py-4'
                  : 'border-l-2 border-l-accent px-5 py-4'
            }
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p
                className="text-[0.8125rem] font-semibold"
                // A failed run is the one thing on this screen a reader must not scroll past, and
                // it can appear without the reader having done anything.
                role={status.state === 'failed' ? 'alert' : undefined}
              >
                {runHeadline(status, formatPeriod(period))}
              </p>
              <Chip tone={runTone(status.state)}>
                {status.state === 'running'
                  ? 'Running'
                  : status.state === 'queued'
                    ? 'Queued'
                    : status.state === 'completed'
                      ? 'Finished'
                      : 'Failed'}
              </Chip>
            </div>

            {runDetail(status) ? (
              <p
                className={
                  status.state === 'failed'
                    ? 'mt-1 text-[0.8125rem] whitespace-pre-line text-danger'
                    : 'mt-1 text-[0.8125rem] text-ink-muted'
                }
              >
                {runDetail(status)}
              </p>
            ) : null}

            {status.state === 'failed' ? (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={run.isPending || (status.send && (!maySend || !replyToRecorded))}
                  onClick={() => run.mutate({ period, send: status.send })}
                >
                  {status.send ? 'Retry production and sending' : 'Retry production'}
                </Button>
              </div>
            ) : null}
            {status.state === 'queued' ? (
              <p className="mt-2 text-[0.75rem] text-ink-muted">
                Waiting for the document worker. If this persists, check worker availability.
                Refreshing this page does not restart the run.
              </p>
            ) : null}
            {runInProgress(status) ? (
              <p className="mt-1 text-[0.8125rem] text-ink-muted">
                Statements appear below as they are produced.
              </p>
            ) : null}

            <p className="mt-2 text-[0.75rem] text-ink-subtle tabular-nums">
              Queued {when(status.enqueuedAt)}
              {status.startedAt === null
                ? ' · No worker start recorded'
                : ` · Last started ${when(status.startedAt)}`}
              {` · ${status.attempts} completed attempts`}
              {status.finishedAt === null ? '' : ` · finished ${when(status.finishedAt)}`}
            </p>
          </Panel>
        ) : null}

        {/* ── What the month comes to ──────────────────────────────────────── */}
        <Panel className="px-5 py-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-[0.9375rem] font-bold">{formatPeriod(period)}</h2>
            <span className="text-[0.75rem] text-ink-subtle tabular-nums">
              {summary.documents} filed
            </span>
          </div>

          <p className="mt-1 text-[0.8125rem] text-ink-muted">{coverageLine(summary)}</p>

          {outstanding ? (
            <p className="mt-2 text-[0.8125rem] font-medium text-warn">{outstanding}</p>
          ) : null}

          {summary.documents > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {(['failed', 'unaddressed', 'queued', 'sent'] as const).map((state) =>
                summary[state] > 0 ? (
                  <Chip key={state} tone={STATE_TONES[state]}>
                    {summary[state]} {STATE_LABELS[state].toLowerCase()}
                  </Chip>
                ) : null,
              )}
            </div>
          ) : null}
        </Panel>

        {/* ── The owners ───────────────────────────────────────────────────── */}
        <Panel className="p-0">
          <PanelHeader
            title="Statements"
            aside={
              <Button
                size="sm"
                variant="secondary"
                disabled={documents.isFetching || runStatus.isFetching}
                onClick={() => {
                  void documents.refetch();
                  void runStatus.refetch();
                }}
              >
                Refresh delivery status
              </Button>
            }
            subtitle="What needs attention first, then everything that worked"
            className="px-5 pt-5 pb-2"
          />

          {documents.isPending ? (
            <QueryLoading label="Loading statements..." />
          ) : documents.isError ? (
            <LoadFailed retry={() => void documents.refetch()} retrying={documents.isFetching} />
          ) : rows.length === 0 ? (
            <p className="border-t border-rule px-5 py-4 text-[0.8125rem] text-ink-muted">
              {/*
               * AN EMPTY LIST HAS TWO CAUSES AND THEY ARE OPPOSITES. "Nobody has run this month"
               * asks the operator to press a button; "the run failed before it filed anything"
               * asks them to fix something first. Telling the second person to press Produce
               * again sends them round the loop that just failed.
               */}
              {status?.state === 'failed'
                ? `The run for ${formatPeriod(period)} failed before it filed anything — the reason is above.`
                : runInProgress(status)
                  ? `Producing ${formatPeriod(period)}. Statements will appear here as they are filed.`
                  : `Nothing has been produced for ${formatPeriod(period)}. Press Produce to file a statement for every owner with activity in the month — nothing is sent to anybody until somebody chooses to.`}
            </p>
          ) : (
            <ul className="flex flex-col">
              {[...rows].sort(byUrgency).map((document) => (
                <DocumentRow key={document.id} document={document} />
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </ReportWorkspace>
  );
}

/** Failures, then owners with nobody to send to, then the rest — see the note on the page. */
const URGENCY: Readonly<Record<DocumentState, number>> = {
  cancelled: 4,
  failed: 0,
  unaddressed: 1,
  queued: 2,
  sent: 3,
};

function byUrgency(a: DeliveryDocument, b: DeliveryDocument): number {
  const order = URGENCY[documentState(a)] - URGENCY[documentState(b)];
  if (order !== 0) return order;
  // Then by name, so a list of thirty-eight successes is still one somebody can find a house in.
  return (a.ownerName ?? '').localeCompare(b.ownerName ?? '', 'en-NZ', { numeric: true });
}

function DocumentRow({ document }: { document: DeliveryDocument }) {
  const state = documentState(document);
  const queryClient = useQueryClient();

  // Out of the bucket, not re-rendered — see `downloadDocument` for why that distinction is the
  // whole reason the file is kept.
  const download = () => downloadDocument(queryClient, document.id);

  return (
    <li className="border-t border-rule px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {document.ownerRef === null ? null : (
          <Ref entity="owner" sequence={Number(document.ownerRef.slice(2))} />
        )}

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-medium">
            {document.ownerName ?? 'Unknown owner'}
          </span>
          <span className="block truncate text-[0.75rem] text-ink-subtle">
            {document.fileName} · {fileSize(document.byteSize)}
          </span>
        </span>

        <Chip tone={STATE_TONES[state]}>{STATE_LABELS[state]}</Chip>

        <ExportPdfButton
          label="Open"
          onExport={download}
          failureTitle="Could not fetch the filed copy"
        />
      </div>

      <DeliveryLines document={document} />
    </li>
  );
}

/**
 * The last N months, newest first, ending at the month before this one.
 *
 * NOT A DATE PICKER. This screen is only ever about a month that has finished, and a control that
 * can select the 14th of a month is a control that invites the question of what that would mean.
 */
function monthsBackFrom(thisMonth: string, count: number): string[] {
  const months: string[] = [];
  let cursor = previousPeriodStart(thisMonth);
  for (let index = 0; index < count; index += 1) {
    months.push(cursor);
    cursor = previousPeriodStart(cursor);
  }
  return months;
}
